import { useEffect, useState, type RefObject } from "react";
import type { LayoutChangeEvent } from "react-native";

/**
 * Whether a scroll area's content is taller than the area: the Dialog's and
 * BottomSheet's Bodies scroll then, and their Footer shows its divider. 1px of
 * tolerance absorbs rounding between the two measurements.
 */
export const isOverflowing = (viewportHeight: number, contentHeight: number) =>
  viewportHeight > 0 && contentHeight > viewportHeight + 1;

// Reports each change, and `false` on unmount, so a Footer doesn't keep the
// divider of a Body that's gone
const useReportOverflow = (overflowing: boolean, onChange?: (overflowing: boolean) => void) => {
  useEffect(() => {
    onChange?.(overflowing);
  }, [overflowing, onChange]);
  useEffect(() => () => onChange?.(false), [onChange]);
};

/**
 * Web: watches a scroll box and everything inside it, and re-measures (once a
 * frame) when any of it resizes, or nodes or attributes change: a window
 * resize, the content growing or shrinking, a nested box growing past a
 * fixed-height parent. Padding counts, through the box's `scrollHeight`.
 */
export function useScrollOverflow(
  viewportRef: RefObject<HTMLElement | null>,
  onChange?: (overflowing: boolean) => void,
) {
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      setOverflowing(isOverflowing(viewport.clientHeight, viewport.scrollHeight));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const resizes = new ResizeObserver(schedule);
    const observeAll = () => {
      resizes.disconnect();
      resizes.observe(viewport);
      viewport.querySelectorAll("*").forEach((element) => resizes.observe(element));
    };
    const mutations = new MutationObserver((records) => {
      if (records.some((record) => record.type === "childList")) observeAll();
      schedule();
    });
    measure();
    observeAll();
    mutations.observe(viewport, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
    });
    return () => {
      cancelAnimationFrame(frame);
      resizes.disconnect();
      mutations.disconnect();
    };
  }, [viewportRef]);

  useReportOverflow(overflowing, onChange);
  return overflowing;
}

/**
 * Native: measures from the layouts of the scroll view and the content view
 * inside it, which fire on every resize. Padding belongs on the content view.
 */
export function useScrollOverflowLayout(onChange?: (overflowing: boolean) => void) {
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const overflowing = isOverflowing(viewportHeight, contentHeight);
  useReportOverflow(overflowing, onChange);
  return {
    overflowing,
    onViewportLayout: (e: LayoutChangeEvent) => setViewportHeight(e.nativeEvent.layout.height),
    onContentLayout: (e: LayoutChangeEvent) => setContentHeight(e.nativeEvent.layout.height),
  };
}
